"use client";
import { useState } from 'react';
import {
    Calculator,
    TrendingUp,
    Clock,
    DollarSign,
    GraduationCap,
    Award,
    Globe
} from 'lucide-react';
import Image from 'next/image';

type Program = {
    value: string;
    label: string;
    pcpsFeePerYear: number;
    duration: number;
    avgSalary: number;
};

type Country = {
    value: string;
    label: string;
    avgTuitionPerYear: number;
    avgLivingPerYear: number;
    avgOtherCosts: number;
    avgSalaryAfterGrad: number;
};

type FormData = {
    program: string;
    country: string;
    pcpsTuitionFeePerYear: number | string;
    pcpsLivingCostPerYear: number | string;
    pcpsAdditionalExpenses: number | string;
    abroadTuitionFee: number | string;
    abroadLivingCost: number | string;
    abroadOtherCosts: number | string;
    studyDuration: number | string;
    pcpsExpectedSalary: number | string;
    abroadExpectedSalary: number | string;
};

type ROIResults = {
    pcps: {
        totalEducationCost: number;
        annualSalary: number;
        roi: number;
        paybackPeriod: number;
    };
    abroad: {
        totalEducationCost: number;
        annualSalary: number;
        roi: number;
        paybackPeriod: number;
    };
};

type ROILevel = {
    level: string;
    color: string;
    textColor: string;
};

const programs: Program[] = [
    {
        value: 'software-engineering',
        label: 'B.Sc. (Hons) Software Engineering',
        pcpsFeePerYear: 403000,
        duration: 3,
        avgSalary: 65000
    },
    {
        value: 'business-management',
        label: 'B.Sc. (Hons) Business Management',
        pcpsFeePerYear: 348000,
        duration: 3,
        avgSalary: 50000
    },
    {
        value: 'business-analytics',
        label: 'B.Sc. (Hons) BM with Business Analytics',
        pcpsFeePerYear: 348000,
        duration: 3,
        avgSalary: 55000
    },
    {
        value: 'digital-marketing',
        label: 'B.Sc. (Hons) BM with Digital Marketing',
        pcpsFeePerYear: 348000,
        duration: 3,
        avgSalary: 52000
    }
];

const countries: Country[] = [
    {
        value: 'australia',
        label: 'Australia',
        avgTuitionPerYear: 3500000,
        avgLivingPerYear: 2200000,
        avgOtherCosts: 300000,
        avgSalaryAfterGrad: 300000
    },
    {
        value: 'uk',
        label: 'United Kingdom',
        avgTuitionPerYear: 3200000,
        avgLivingPerYear: 2000000,
        avgOtherCosts: 250000,
        avgSalaryAfterGrad: 300000
    },
    {
        value: 'canada',
        label: 'Canada',
        avgTuitionPerYear: 2800000,
        avgLivingPerYear: 1800000,
        avgOtherCosts: 200000,
        avgSalaryAfterGrad: 300000
    },
    {
        value: 'usa',
        label: 'United States',
        avgTuitionPerYear: 4500000,
        avgLivingPerYear: 2500000,
        avgOtherCosts: 400000,
        avgSalaryAfterGrad: 300000
    }
];

const initialFormData: FormData = {
    program: '',
    country: '',
    pcpsTuitionFeePerYear: '',
    pcpsLivingCostPerYear: 150000, // Default living cost in Nepal
    pcpsAdditionalExpenses: 5000,
    abroadTuitionFee: '',
    abroadLivingCost: '',
    abroadOtherCosts: '',
    studyDuration: 3,
    pcpsExpectedSalary: '',
    abroadExpectedSalary: ''
};

const StudentROICalculator: React.FC = () => {
    const [formData, setFormData] = useState<FormData>(initialFormData);
    const [results, setResults] = useState<ROIResults | null>(null);

    const handleInputChange = (
        field: keyof FormData,
        value: string | number
    ) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Auto-populate fields when program is selected
        if (field === 'program' && value !== '') {
            const selectedProgram = programs.find(p => p.value === value);
            if (selectedProgram) {
                setFormData(prev => ({
                    ...prev,
                    pcpsTuitionFeePerYear: selectedProgram.pcpsFeePerYear,
                    studyDuration: selectedProgram.duration,
                    pcpsExpectedSalary: selectedProgram.avgSalary
                }));
            }
        }

        // Auto-populate fields when country is selected
        if (field === 'country' && value !== '') {
            const selectedCountry = countries.find(c => c.value === value);
            if (selectedCountry) {
                setFormData(prev => ({
                    ...prev,
                    abroadTuitionFee: selectedCountry.avgTuitionPerYear,
                    abroadLivingCost: selectedCountry.avgLivingPerYear,
                    abroadOtherCosts: selectedCountry.avgOtherCosts,
                    abroadExpectedSalary: selectedCountry.avgSalaryAfterGrad
                }));
            }
        }
    };

    const calculateROI = () => {
        const pcpsTuitionPerYear = parseFloat(formData.pcpsTuitionFeePerYear as string) || 0;
        const pcpsLivingPerYear = parseFloat(formData.pcpsLivingCostPerYear as string) || 0;
        const pcpsAdditional = parseFloat(formData.pcpsAdditionalExpenses as string) || 0;
        const abroadTuition = parseFloat(formData.abroadTuitionFee as string) || 0;
        const abroadLiving = parseFloat(formData.abroadLivingCost as string) || 0;
        const abroadOther = parseFloat(formData.abroadOtherCosts as string) || 0;
        const duration = parseFloat(formData.studyDuration as string) || 3;
        const pcpsSalary = parseFloat(formData.pcpsExpectedSalary as string) || 0;
        const abroadSalary = parseFloat(formData.abroadExpectedSalary as string) || 0;

        if (
            !formData.program ||
            !formData.country ||
            pcpsTuitionPerYear === 0 ||
            pcpsSalary === 0 ||
            abroadSalary === 0
        ) {
            alert('Please fill in all required fields');
            return;
        }

        // PCPS Calculations
        const pcpsTotalEducationCost =
            pcpsTuitionPerYear * duration +
            pcpsLivingPerYear * duration +
            pcpsAdditional;
        const pcpsAnnualSalary = pcpsSalary * 12;
        const pcpsROI =
            ((pcpsAnnualSalary * 3 - pcpsTotalEducationCost) / pcpsTotalEducationCost) *
            100;
        const pcpsPaybackPeriod = pcpsTotalEducationCost / pcpsAnnualSalary;

        // Abroad Calculations
        const abroadTotalEducationCost =
            abroadTuition * duration + abroadLiving * duration + abroadOther;
        const abroadAnnualSalary = abroadSalary * 12;
        const abroadROI =
            ((abroadAnnualSalary * 3 - abroadTotalEducationCost) /
                abroadTotalEducationCost) *
            100;
        const abroadPaybackPeriod = abroadTotalEducationCost / abroadAnnualSalary;

        setResults({
            pcps: {
                totalEducationCost: pcpsTotalEducationCost,
                annualSalary: pcpsAnnualSalary,
                roi: pcpsROI,
                paybackPeriod: pcpsPaybackPeriod
            },
            abroad: {
                totalEducationCost: abroadTotalEducationCost,
                annualSalary: abroadAnnualSalary,
                roi: abroadROI,
                paybackPeriod: abroadPaybackPeriod
            }
        });
    };

    const getROILevel = (roi: number): ROILevel => {
        if (roi >= 100)
            return {
                level: 'High',
                color: 'bg-green-500',
                textColor: 'text-green-700'
            };
        if (roi >= 50)
            return {
                level: 'Medium',
                color: 'bg-yellow-500',
                textColor: 'text-yellow-700'
            };
        return {
            level: 'Low',
            color: 'bg-red-500',
            textColor: 'text-red-700'
        };
    };

    const formatCurrency = (amount: number | string): string => {
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;
        return new Intl.NumberFormat('en-NP', {
            style: 'currency',
            currency: 'NPR',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0
        }).format(num);
    };

    const resetCalculator = () => {
        setFormData({
            program: '',
            country: '',
            pcpsTuitionFeePerYear: '',
            pcpsLivingCostPerYear: 200000,
            pcpsAdditionalExpenses: 50000,
            abroadTuitionFee: '',
            abroadLivingCost: '',
            abroadOtherCosts: '',
            studyDuration: 3,
            pcpsExpectedSalary: '',
            abroadExpectedSalary: ''
        });
        setResults(null);
    };

    return (
        <div className="">
            {/* Header */}
            <div className="bg-white ">
                <div className="max-w-7xl mx-auto px-6 py-8">
                    <div className="text-center">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-mitRed rounded-full flex items-center justify-center">
                                <Calculator className="size-8 text-primary-red" />
                            </div>
                            <h1 className="text-4xl font-bold text-primary-red">
                                Student ROI Calculator
                            </h1>
                        </div>
                        <p className="text-lg max-w-4xl mx-auto">
                            Evaluate the financial value of different academic programs and make informed decisions about your education investment.
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-12">
                {/* Program and Country Selection */}
                <div className="grid gap-8 lg:grid-cols-2 mb-8">
                    {/* Program Selection */}
                    <div className="bg-white rounded-2xl shadow-lg p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <GraduationCap className="w-6 h-6 text-primary-red" />
                            <h2 className="text-2xl font-semibold text-primary-red">
                                Select Program
                            </h2>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-mitBlack mb-2">
                                    Choose Your Program *
                                </label>
                                <select
                                    value={formData.program}
                                    onChange={e =>
                                        handleInputChange('program', e.target.value)
                                    }
                                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mitRed focus:border-transparent"
                                    required
                                >
                                    <option value="">Select a program...</option>
                                    {programs.map(program => (
                                        <option key={program.value} value={program.value}>
                                            {program.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {formData.program && (
                                <div className="bg-white rounded-lg p-4">
                                    <h4 className="font-semibold text-mitBlack mb-2">
                                        Program Details
                                    </h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="">Duration:</span>
                                            <span className="font-medium text-mitBlack">
                                                {formData.studyDuration} years
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="">PCPS Fee/year:</span>
                                            <span className="font-medium text-mitRed">
                                                {formatCurrency(formData.pcpsTuitionFeePerYear)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="">Living/year:</span>
                                            <span className="font-medium ">
                                                {formatCurrency(formData.pcpsLivingCostPerYear)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="">Total Cost:</span>
                                            <span className="font-medium text-primary-red">
                                                {formatCurrency(
                                                    (Number(formData.pcpsTuitionFeePerYear) * Number(formData.studyDuration)) +
                                                    (Number(formData.pcpsLivingCostPerYear) * Number(formData.studyDuration)) +
                                                    Number(formData.pcpsAdditionalExpenses)
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Country Selection */}
                    <div className="bg-white rounded-2xl shadow-lg p-8">
                        <div className="flex items-center gap-3 mb-6">
                            <Globe className="w-6 h-6 text-primary-blue" />
                            <h2 className="text-2xl font-semibold text-primary-blue">
                                Compare with Country
                            </h2>
                        </div>

                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-semibold text-mitBlack mb-2">
                                    Select Country *
                                </label>
                                <select
                                    value={formData.country}
                                    onChange={e =>
                                        handleInputChange('country', e.target.value)
                                    }
                                    className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                    required
                                >
                                    <option value="">Select a country...</option>
                                    {countries.map(country => (
                                        <option key={country.value} value={country.value}>
                                            {country.label}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            {formData.country && (
                                <div className="bg-blue-50 rounded-lg p-4">
                                    <h4 className="font-semibold text-mitBlack mb-2">
                                        Estimated Costs
                                    </h4>
                                    <div className="space-y-2 text-sm">
                                        <div className="flex justify-between">
                                            <span className="text-blue-700">Duration:</span>
                                            <span className="font-medium text-mitBlack">
                                                {formData.studyDuration} years
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-blue-700">Tuition/year:</span>
                                            <span className="font-medium text-mitBlack">
                                                {formatCurrency(formData.abroadTuitionFee)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-blue-700">Living/year:</span>
                                            <span className="font-medium text-mitBlack">
                                                {formatCurrency(formData.abroadLivingCost)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-blue-700">Other costs:</span>
                                            <span className="font-medium text-mitBlack">
                                                {formatCurrency(formData.abroadOtherCosts)}
                                            </span>
                                        </div>
                                        <div className="flex justify-between">
                                            <span className="text-blue-700">Total Cost:</span>
                                            <span className="font-medium text-blue-600">
                                                {formatCurrency(
                                                    (Number(formData.abroadTuitionFee) * Number(formData.studyDuration)) +
                                                    (Number(formData.abroadLivingCost) * Number(formData.studyDuration)) +
                                                    Number(formData.abroadOtherCosts)
                                                )}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Detailed Input Forms */}
                {formData.program && formData.country && (
                    <div className="grid gap-8 lg:grid-cols-2 mb-8">
                        {/* PCPS College Details */}
                        <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-primary-red">
                            <div className="flex items-center gap-3 mb-6">
                                {/* <img src="/pcpslogo.png" alt="PCPS" className="w-16 h-8" /> */}
                                <Image
                                    src={"/pcpslogo.png"}
                                    alt="PCPS College Logo"
                                    width={92}
                                    height={60}
                                    className='object-cover'
                                />
                                <h3 className="text-xl font-display text-mitBlack">
                                    PCPS College (Nepal)
                                </h3>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-semibold text-mitBlack mb-2">
                                        Study Duration (Years) *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.studyDuration}
                                        onChange={e =>
                                            handleInputChange('studyDuration', e.target.value)
                                        }
                                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mitRed focus:border-transparent"
                                        placeholder="Enter study duration"
                                        min={1}
                                        max={6}
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-mitBlack mb-2">
                                        Tuition Fee per Year (NPR) *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.pcpsTuitionFeePerYear}
                                        onChange={e =>
                                            handleInputChange('pcpsTuitionFeePerYear', e.target.value)
                                        }
                                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mitRed focus:border-transparent"
                                        placeholder="Enter tuition fee per year"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-mitBlack mb-2">
                                        Living Cost per Year (NPR) *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.pcpsLivingCostPerYear}
                                        onChange={e =>
                                            handleInputChange('pcpsLivingCostPerYear', e.target.value)
                                        }
                                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mitRed focus:border-transparent"
                                        placeholder="Enter living cost per year"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-mitBlack mb-2">
                                        Other Costs (Books, Exams, etc.)
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.pcpsAdditionalExpenses}
                                        onChange={e =>
                                            handleInputChange('pcpsAdditionalExpenses', e.target.value)
                                        }
                                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mitRed focus:border-transparent"
                                        placeholder="Enter other costs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-mitBlack mb-2">
                                        Expected Monthly Salary (NPR) *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.pcpsExpectedSalary}
                                        onChange={e =>
                                            handleInputChange('pcpsExpectedSalary', e.target.value)
                                        }
                                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-mitRed focus:border-transparent"
                                        placeholder="Enter expected monthly salary"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Abroad University Details */}
                        <div className="bg-white rounded-2xl shadow-lg p-8 border-l-4 border-primary-blue">
                            <div className="flex items-center gap-3 mb-6">
                                <Globe className="w-8 h-8 text-blue-600" />
                                <h3 className="text-xl font-display text-mitBlack">
                                    Study in {countries.find(c => c.value === formData.country)?.label}
                                </h3>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <label className="block text-sm font-semibold text-mitBlack mb-2">
                                        Study Duration (Years) *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.studyDuration}
                                        onChange={e =>
                                            handleInputChange('studyDuration', e.target.value)
                                        }
                                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                        placeholder="Enter study duration"
                                        min={1}
                                        max={6}
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-mitBlack mb-2">
                                        Tuition Fee per Year (NPR) *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.abroadTuitionFee}
                                        onChange={e =>
                                            handleInputChange('abroadTuitionFee', e.target.value)
                                        }
                                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                        placeholder="Enter tuition fee per year"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-mitBlack mb-2">
                                        Living Cost per Year (NPR) *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.abroadLivingCost}
                                        onChange={e =>
                                            handleInputChange('abroadLivingCost', e.target.value)
                                        }
                                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                        placeholder="Enter living cost per year"
                                        required
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-mitBlack mb-2">
                                        Other Costs (Visa, Travel, etc.)
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.abroadOtherCosts}
                                        onChange={e =>
                                            handleInputChange('abroadOtherCosts', e.target.value)
                                        }
                                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                        placeholder="Enter other costs"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-semibold text-mitBlack mb-2">
                                        Expected Monthly Salary (NPR) *
                                    </label>
                                    <input
                                        type="number"
                                        value={formData.abroadExpectedSalary}
                                        onChange={e =>
                                            handleInputChange('abroadExpectedSalary', e.target.value)
                                        }
                                        className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-600 focus:border-transparent"
                                        placeholder="Enter expected monthly salary"
                                        required
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Calculate Button */}
                {formData.program && formData.country && (
                    <div className="text-center mb-8">
                        <div className="flex gap-4 justify-center">
                            <button
                                onClick={calculateROI}
                                className="bg-gradient-to-r from-mitRed to-mitCranberry hover:from-mitCranberry hover:to-mitRed text-white font-semibold py-4 px-12 rounded-lg transition-all flex items-center gap-3 text-lg shadow-lg hover:shadow-xl transform hover:-translate-y-1"
                            >
                                <Calculator className="w-6 h-6" />
                                Calculate & Compare ROI
                            </button>
                            <button
                                onClick={resetCalculator}
                                className="px-8 py-4 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors font-semibold"
                            >
                                Reset
                            </button>
                        </div>
                    </div>
                )}

                {/* Results */}
                {results && (
                    <div className="space-y-8">
                        {/* Comparison Header */}
                        <div className="text-center">
                            <h2 className="text-3xl font-display text-mitBlack mb-4">
                                ROI Comparison Results
                            </h2>
                            <p className="text-mitStone text-lg">
                                PCPS College vs {countries.find(c => c.value === formData.country)?.label}
                            </p>
                        </div>

                        {/* Side by Side Comparison */}
                        <div className="grid gap-8 lg:grid-cols-2">
                            {/* PCPS Results */}
                            <div
                                className={`bg-white rounded-2xl shadow-lg p-8 border-l-4 ${results.pcps.roi > results.abroad.roi
                                    ? 'border-green-500 bg-green-50/30'
                                    : 'border-mitRed'
                                    }`}
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-3">
                                        <Image
                                            src={"/pcpslogo.png"}
                                            alt="PCPS College Logo"
                                            width={92}
                                            height={60}
                                            className='object-cover'
                                        />
                                        <div>
                                            <h3 className="text-xl font-display text-mitBlack">
                                                PCPS College
                                            </h3>
                                            <p className="text-sm text-mitStone">Nepal</p>
                                        </div>
                                    </div>
                                    {results.pcps.roi > results.abroad.roi && (
                                        <div className="flex items-center gap-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                            <Award className="w-4 h-4" />
                                            Better ROI
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    <div className="bg-mitSand/20 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <DollarSign className="w-5 h-5 text-mitRed" />
                                            <span className="text-sm font-semibold text-mitBlack">
                                                Total Education Cost
                                            </span>
                                        </div>
                                        <p className="text-2xl font-bold text-mitRed">
                                            {formatCurrency(results.pcps.totalEducationCost)}
                                        </p>
                                    </div>

                                    <div className="bg-mitSand/20 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <TrendingUp className="w-5 h-5 text-mitRed" />
                                            <span className="text-sm font-semibold text-mitBlack">
                                                Expected Annual Income
                                            </span>
                                        </div>
                                        <p className="text-2xl font-bold text-green-600">
                                            {formatCurrency(results.pcps.annualSalary)}
                                        </p>
                                    </div>

                                    <div className="bg-mitSand/20 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Award className="w-5 h-5 text-mitRed" />
                                            <span className="text-sm font-semibold text-mitBlack">
                                                ROI Percentage
                                            </span>
                                        </div>
                                        <p className="text-3xl font-bold text-green-600">
                                            {results.pcps.roi.toFixed(1)}%
                                        </p>
                                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                            <div
                                                className="bg-green-500 h-2 rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${Math.min(Math.max(results.pcps.roi, 0), 200) / 2
                                                        }%`
                                                }}
                                            ></div>
                                        </div>
                                    </div>

                                    <div className="bg-mitSand/20 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Clock className="w-5 h-5 text-mitRed" />
                                            <span className="text-sm font-semibold text-mitBlack">
                                                Payback Period
                                            </span>
                                        </div>
                                        <p className="text-2xl font-bold text-blue-600">
                                            {results.pcps.paybackPeriod.toFixed(1)} years
                                        </p>
                                    </div>
                                </div>
                            </div>

                            {/* Abroad Results */}
                            <div
                                className={`bg-white rounded-2xl shadow-lg p-8 border-l-4 ${results.abroad.roi > results.pcps.roi
                                    ? 'border-green-500 bg-green-50/30'
                                    : 'border-blue-500'
                                    }`}
                            >
                                <div className="flex items-center justify-between mb-6">
                                    <div className="flex items-center gap-3">
                                        <Globe className="w-10 h-10 text-blue-500" />
                                        <div>
                                            <h3 className="text-xl font-display text-mitBlack">
                                                {
                                                    countries.find(
                                                        c => c.value === formData.country
                                                    )?.label
                                                }
                                            </h3>
                                            <p className="text-sm text-mitStone">
                                                International Study
                                            </p>
                                        </div>
                                    </div>
                                    {results.abroad.roi > results.pcps.roi && (
                                        <div className="flex items-center gap-2 bg-green-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                                            <Award className="w-4 h-4" />
                                            Better ROI
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    <div className="bg-blue-50 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <DollarSign className="w-5 h-5 text-blue-500" />
                                            <span className="text-sm font-semibold text-mitBlack">
                                                Total Education Cost
                                            </span>
                                        </div>
                                        <p className="text-2xl font-bold text-blue-500">
                                            {formatCurrency(results.abroad.totalEducationCost)}
                                        </p>
                                    </div>

                                    <div className="bg-blue-50 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <TrendingUp className="w-5 h-5 text-blue-500" />
                                            <span className="text-sm font-semibold text-mitBlack">
                                                Expected Annual Income
                                            </span>
                                        </div>
                                        <p className="text-2xl font-bold text-green-600">
                                            {formatCurrency(results.abroad.annualSalary)}
                                        </p>
                                    </div>

                                    <div className="bg-blue-50 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Award className="w-5 h-5 text-blue-500" />
                                            <span className="text-sm font-semibold text-mitBlack">
                                                ROI Percentage
                                            </span>
                                        </div>
                                        <p className="text-3xl font-bold text-green-600">
                                            {results.abroad.roi.toFixed(1)}%
                                        </p>
                                        <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
                                            <div
                                                className="bg-green-500 h-2 rounded-full transition-all duration-500"
                                                style={{
                                                    width: `${Math.min(Math.max(results.abroad.roi, 0), 200) / 2
                                                        }%`
                                                }}
                                            ></div>
                                        </div>
                                    </div>

                                    <div className="bg-blue-50 rounded-lg p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <Clock className="w-5 h-5 text-blue-500" />
                                            <span className="text-sm font-semibold text-mitBlack">
                                                Payback Period
                                            </span>
                                        </div>
                                        <p className="text-2xl font-bold text-blue-600">
                                            {results.abroad.paybackPeriod.toFixed(1)} years
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Summary Comparison */}
                        <div className="bg-white rounded-2xl shadow-lg p-8">
                            <h3 className="text-2xl font-display text-mitBlack mb-6 text-center">
                                Investment Comparison Summary
                            </h3>

                            <div className="grid gap-6 md:grid-cols-3">
                                <div className="text-center p-6 bg-gradient-to-br from-green-50 to-green-100 rounded-xl border border-green-200">
                                    <Award className="w-8 h-8 text-green-600 mx-auto mb-3" />
                                    <h4 className="font-semibold text-mitBlack mb-2">
                                        🏆 Higher ROI
                                    </h4>
                                    <p className="text-lg font-bold text-green-600">
                                        {results.pcps.roi > results.abroad.roi
                                            ? 'PCPS College'
                                            : countries.find(
                                                c => c.value === formData.country
                                            )?.label}
                                    </p>
                                    <p className="text-sm text-green-700 mt-1">
                                        {results.pcps.roi > results.abroad.roi
                                            ? results.pcps.roi.toFixed(1)
                                            : results.abroad.roi.toFixed(1)}
                                        % ROI
                                    </p>
                                </div>

                                <div className="text-center p-6 bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl border border-blue-200">
                                    <Clock className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                                    <h4 className="font-semibold text-mitBlack mb-2">
                                        ⏱ Faster Payback
                                    </h4>
                                    <p className="text-lg font-bold text-blue-600">
                                        {results.pcps.paybackPeriod < results.abroad.paybackPeriod
                                            ? 'PCPS College'
                                            : countries.find(
                                                c => c.value === formData.country
                                            )?.label}
                                    </p>
                                    <p className="text-sm text-blue-700 mt-1">
                                        {results.pcps.paybackPeriod < results.abroad.paybackPeriod
                                            ? results.pcps.paybackPeriod.toFixed(1)
                                            : results.abroad.paybackPeriod.toFixed(1)}{' '}
                                        years
                                    </p>
                                </div>

                                <div className="text-center p-6 bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl border border-purple-200">
                                    <DollarSign className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                                    <h4 className="font-semibold text-mitBlack mb-2">
                                        💰 Lower Investment
                                    </h4>
                                    <p className="text-lg font-bold text-purple-600">
                                        {results.pcps.totalEducationCost <
                                            results.abroad.totalEducationCost
                                            ? 'PCPS College'
                                            : countries.find(
                                                c => c.value === formData.country
                                            )?.label}
                                    </p>
                                    <p className="text-sm text-purple-700 mt-1">
                                        {formatCurrency(
                                            results.pcps.totalEducationCost <
                                                results.abroad.totalEducationCost
                                                ? results.pcps.totalEducationCost
                                                : results.abroad.totalEducationCost
                                        )}
                                    </p>
                                </div>
                            </div>

                            {/* Cost Savings Highlight */}
                            <div className="mt-6 p-6 bg-gradient-to-r from-mitRed/10 to-mitCranberry/10 rounded-xl border border-mitRed/20">
                                <h4 className="font-semibold text-mitBlack mb-3 text-center">
                                    💡 Cost Savings Analysis
                                </h4>
                                <div className="grid gap-4 md:grid-cols-2">
                                    <div className="text-center">
                                        <p className="text-sm text-mitStone">
                                            Total Cost Difference
                                        </p>
                                        <p className="text-2xl font-bold text-mitRed">
                                            {formatCurrency(
                                                Math.abs(
                                                    results.abroad.totalEducationCost -
                                                    results.pcps.totalEducationCost
                                                )
                                            )}
                                        </p>
                                        <p className="text-xs text-mitStone">
                                            {results.pcps.totalEducationCost <
                                                results.abroad.totalEducationCost
                                                ? 'Savings with PCPS'
                                                : 'Additional cost with PCPS'}
                                        </p>
                                    </div>
                                    <div className="text-center">
                                        <p className="text-sm text-mitStone">ROI Difference</p>
                                        <p className="text-2xl font-bold text-mitRed">
                                            {Math.abs(results.pcps.roi - results.abroad.roi).toFixed(
                                                1
                                            )}
                                            %
                                        </p>
                                        <p className="text-xs text-mitStone">
                                            {results.pcps.roi > results.abroad.roi
                                                ? 'Higher with PCPS'
                                                : 'Higher abroad'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Placeholder when no results */}
                {!results && !formData.program && (
                    <div className="bg-white rounded-2xl shadow-lg p-8 flex flex-col items-center justify-center text-center">
                        <div className="w-24 h-24 bg-gradient-to-br from-mitRed/20 to-mitCranberry/20 rounded-full flex items-center justify-center mb-6">
                            <Calculator className="w-12 h-12 text-mitRed" />
                        </div>
                        <h3 className="text-2xl font-display text-mitBlack mb-3">
                            Compare Education Investment
                        </h3>
                        <p className="text-mitStone mb-6 max-w-md leading-relaxed">
                            Select your program and compare the ROI of studying at PCPS College versus international universities.
                        </p>
                        <div className="grid grid-cols-2 gap-6 text-sm">
                            <div className="text-center p-4 bg-mitSand/20 rounded-lg">
                                <div className="w-8 h-8 bg-mitRed rounded-full flex items-center justify-center mx-auto mb-2">
                                    <span className="text-white font-bold text-sm">1</span>
                                </div>
                                <p className="font-medium text-mitBlack">Select Program</p>
                                <p className="text-xs text-mitStone">
                                    Choose your PCPS program
                                </p>
                            </div>
                            <div className="text-center p-4 bg-blue-50 rounded-lg">
                                <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2">
                                    <span className="text-white font-bold text-sm">2</span>
                                </div>
                                <p className="font-medium text-mitBlack">Select Country</p>
                                <p className="text-xs text-mitStone">Compare with abroad</p>
                            </div>
                            <div className="text-center p-4 bg-green-50 rounded-lg">
                                <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2">
                                    <span className="text-white font-bold text-sm">3</span>
                                </div>
                                <p className="font-medium text-mitBlack">Review Costs</p>
                                <p className="text-xs text-mitStone">Adjust if needed</p>
                            </div>
                            <div className="text-center p-4 bg-purple-50 rounded-lg">
                                <div className="w-8 h-8 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-2">
                                    <span className="text-white font-bold text-sm">4</span>
                                </div>
                                <p className="font-medium text-mitBlack">Compare ROI</p>
                                <p className="text-xs text-mitStone">See the results</p>
                            </div>
                        </div>
                    </div>
                )}

                {/* Disclaimer */}
                <div className="bg-yellow-50 border border-yellow-200 rounded-2xl p-6 mt-8">
                    <div className="flex items-start gap-3">
                        <div className="w-6 h-6 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                            <span className="text-white text-sm font-bold">!</span>
                        </div>
                        <div>
                            <h4 className="font-semibold text-yellow-800 mb-2">
                                Important Disclaimer
                            </h4>
                            <p className="text-yellow-700 text-sm leading-relaxed">
                                This ROI calculator provides estimated results for guidance only. Actual salaries may vary depending on
                                skills, experience, industry demand, and job market conditions. Use this tool as a starting point for
                                your educational planning and consider consulting with academic advisors for personalized guidance.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default StudentROICalculator;